import React, { useState, useEffect } from 'react';
import {
  Container, Paper, Typography, TextField, Grid, Checkbox, FormControlLabel,
  MenuItem, Button, Select, InputLabel, FormControl, Autocomplete, Accordion, AccordionSummary, AccordionDetails, Box,
  Alert, CircularProgress, Divider, FormHelperText
} from '@mui/material';
import { API_BASE_URL } from '../../lib/constants';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EmailIcon from '@mui/icons-material/Email';
import axios from 'axios';
import { useNavigate, useParams } from 'react-router-dom';
import theme from '../../theme/theme';
import DraftSaveButton from '../../components/DraftSaveButton';
import { DRAFT_KEYS, loadDraft, hasDraft, clearDraft, saveDraft } from '../../utils/draftUtils';
import { ThemeProvider } from '@mui/material/styles';
import { useNotification } from '../../contexts/NotificationContext';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import { Link } from 'react-router-dom';

const bloodComponents = [
  'Whole Blood', 'Packed Cells', 'Platelets', 'Fresh Frozen Plasma', 'Cryoprecipitate'
];

const labs = ['CBC', 'PT/INR', 'Electrolytes', 'Crossmatch'];
const imaging = ['X-Ray', 'CT', 'MRI', 'Echo'];
const antibiotics = ['Cefazolin', 'Cefuroxime', 'Ampicillin', 'Vancomycin'];
const antiseptics = ['Povidone-iodine', 'Chlorhexidine'];
const bridgingPlans = ['LMWH', 'Heparin Infusion', 'None'];
const positioningOptions = ['Supine', 'Prone', 'Lateral', 'Lithotomy'];

// Blood group options with proper types
const bloodGroups = ['A+', 'A-', 'B+', 'B-', 'AB+', 'AB-', 'O+', 'O-'];

// Common patient names for auto-suggestion
const commonNames = [
  'Rajesh Kumar', 'Priya Sharma', 'Arjun Patel', 'Sneha Reddy', 'Vikram Singh',
  'Anitha Krishnan', 'Suresh Gupta', 'Meera Nair', 'Karthik Iyer', 'Divya Joshi'
];

const PreoperativeOrders = () => {
  const { showNotification } = useNotification();
  const { uhid_no } = useParams();
  const [success, setSuccess] = useState("");
  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [validationErrors, setValidationErrors] = useState({});
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [filteredPatients, setFilteredPatients] = useState([]);
  const [otBookings, setOtBookings] = useState([]);
  const [data, setData] = useState([]);
  const [emailLoading, setEmailLoading] = useState(false);
  const [emailSuccess, setEmailSuccess] = useState("");

  const surgeryData = [
    {
      icd10Code: "K80.0",
      diagnosisDescription: "Calculus of gallbladder with acute cholecystitis",
      procedureCode: "47562",
      procedureDescription: "Laparoscopic cholecystectomy"
    },
    {
      icd10Code: "K35.80",
      diagnosisDescription: "Acute appendicitis",
      procedureCode: "44950",
      procedureDescription: "Appendectomy"
    },
    {
      icd10Code: "N20.0",
      diagnosisDescription: "Kidney stone",
      procedureCode: "50590",
      procedureDescription: "Lithotripsy, extracorporeal"
    },
    {
      icd10Code: "M17.11",
      diagnosisDescription: "Osteoarthritis of right knee",
      procedureCode: "27447",
      procedureDescription: "Total knee arthroplasty (replacement)"
    },
    {
      icd10Code: "M16.11",
      diagnosisDescription: "Osteoarthritis of right hip",
      procedureCode: "27130",
      procedureDescription: "Total hip replacement"
    },
    {
      icd10Code: "C50.911",
      diagnosisDescription: "Breast cancer, right breast",
      procedureCode: "19301",
      procedureDescription: "Lumpectomy"
    },
    {
      icd10Code: "I25.10",
      diagnosisDescription: "Coronary artery disease",
      procedureCode: "33533",
      procedureDescription: "Coronary artery bypass grafting (CABG), single"
    },
    {
      icd10Code: "D25.0",
      diagnosisDescription: "Submucous uterine fibroid",
      procedureCode: "58150",
      procedureDescription: "Total abdominal hysterectomy"
    },
    {
      icd10Code: "K40.20",
      diagnosisDescription: "Inguinal hernia",
      procedureCode: "49505",
      procedureDescription: "Repair of inguinal hernia, initial"
    },
    {
      icd10Code: "L03.115",
      diagnosisDescription: "Cellulitis of right lower limb",
      procedureCode: "11042",
      procedureDescription: "Debridement, skin and subcutaneous tissue"
    },
    {
      icd10Code: "S82.201A",
      diagnosisDescription: "Fracture of tibia shaft",
      procedureCode: "27758",
      procedureDescription: "Open treatment of tibial shaft fracture"
    }
  ];

  const [formData, setFormData] = useState({
    // Section 1: Auto-filled details
    uhid: parseInt(uhid_no) || 0,
    ipNumber: 0,
    name: '',
    hospital_id: 1,
    age: '',
    sex: '',
    room: '',
    diagnosis: '',
    proposedSurgery: '',
    scheduledDatetime: '',
    surgeonName: '',
    surgical_code: '',
    procedure_code: '',
    procedureDescription: '',

    // Section 2: Consents
    surgicalConsent: false,
    highRiskConsent: false,
    bloodTransfusionConsent: false,
    anesthesiaConsent: false,
    guardianName: '',
    guardianContact: '',
    consentComments: '',

    // Section 3: Blood orders
    bloodOrders: {},
    sopNeeded: false,
    bloodGroup: '',
    prdcAttached: false,
    transfusionNotes: '',

    // Section 4: Antibiotic & Medications
    antibiotic: '',
    dose: '',
    timeToAdminister: '',
    additionalAntibiotics: '',
    skinPrep: '',
    preAnestheticMeds: '',
    preOpSedation: false,

    // Section 5: Anticoagulation
    onAnticoagulants: false,
    lastDose: '',
    bridgingPlan: '',
    stopDate: '',
    postOpThromboprophylaxis: false,
    allergyReview: '',

    // Section 6: Investigations
    labsToDo: [],
    imagingToBeAvailable: [],
    cardiologyClearance: false,
    anesthClearance: false,
    otherSpecialtyClearance: '',

    // Section 7: Surgeon instructions
    additionalEquipments: '',
    criticalNotes: '',
    estimatedBloodLoss: '',
    siteMarking: false,
    positioning: '',
    surgeonNotes: '',

    // Section 8: Confirmation
    finalized: false
  });

  const roomNumbers = [
    'OT-1', 'OT-2', 'OT-3', 'OT-4', 'OT-5',
    'ICU-1', 'ICU-2', 'Recovery-1', 'Recovery-2'
  ];

  // Utility functions for date validation
  const getCurrentDateTime = () => {
    const now = new Date();
    return now.toISOString().slice(0, 16);
  };

  const getTodayDate = () => {
    const today = new Date();
    return today.toISOString().slice(0, 10);
  };

  const getNextDay = (dateString) => {
    const date = new Date(dateString);
    date.setDate(date.getDate() + 1);
    return date.toISOString().slice(0, 10);
  };

  const validateField = (name, value) => {
    let error = '';
    const today = getTodayDate();
    const now = getCurrentDateTime();

    switch (name) {
      case 'scheduledDatetime':
        if (value && value < now) {
          error = 'Surgery cannot be scheduled in the past';
        }
        break;

      case 'lastDose':
        if (value && value > now) {
          error = 'Last dose cannot be in the future';
        }
        break;

      case 'stopDate':
        if (value) {
          if (value < today) {
            error = 'Stop date cannot be in the past';
          }
          if (formData.scheduledDatetime) {
            const surgeryDate = formData.scheduledDatetime.slice(0, 10);
            if (value > surgeryDate) {
              error = 'Stop date should be before or on surgery date';
            }
          }
        }
        break;

      case 'age':
        const ageNum = parseInt(value);
        if (value && (ageNum < 0 || ageNum > 150)) {
          error = 'Please enter a valid age between 0-150';
        }
        break;

      case 'guardianContact':
        if (value && value.length < 10) {
          error = 'Please enter a valid phone number';
        }
        break;

      case 'estimatedBloodLoss':
        const bloodLoss = parseInt(value);
        if (value && (bloodLoss < 0 || bloodLoss > 10000)) {
          error = 'Please enter a realistic blood loss estimate (0-10000ml)';
        }
        break;

      default:
        break;
    }

    setValidationErrors(prev => ({
      ...prev,
      [name]: error
    }));

    return error === '';
  };

  const handleBlur = (event) => {
    const { name, value } = event.target;
    validateField(name, value);
  };

  // Auto-calculate stop date based on surgery date
  useEffect(() => {
    if (formData.scheduledDatetime && formData.onAnticoagulants) {
      const surgeryDate = formData.scheduledDatetime.slice(0, 10);
      const suggestedStopDate = getNextDay(surgeryDate);

      // Only auto-set if no stop date is already set
      if (!formData.stopDate) {
        setFormData(prev => ({
          ...prev,
          stopDate: suggestedStopDate
        }));
      }
    }
  }, [formData.scheduledDatetime, formData.onAnticoagulants]);

  // Load existing data when component mounts
  useEffect(() => {
    if (uhid_no) {
      loadExistingData();
      fetchPatientData();
    }
  }, [uhid_no]);

  // Auto-load draft if exists
  useEffect(() => {
    if (uhid_no && hasDraft(DRAFT_KEYS.PREOPERATIVE_ORDERS, uhid_no)) {
      const saved = loadDraft(DRAFT_KEYS.PREOPERATIVE_ORDERS, uhid_no);
      if (saved && saved.formData) {
        setFormData(saved.formData);
      }
    }
  }, [uhid_no]);

  // Auto-save draft
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (uhid_no) {
        saveDraft(DRAFT_KEYS.PREOPERATIVE_ORDERS, uhid_no, formData);
      }
    }, 2000);
    return () => clearTimeout(timeoutId);
  }, [formData, uhid_no]);

  // Auto-calculate administration time based on dose
  useEffect(() => {
    if (formData.dose) {
      const now = new Date();
      const doseInMinutes = parseInt(formData.dose, 10);
      if (!isNaN(doseInMinutes)) {
        now.setMinutes(now.getMinutes() + doseInMinutes);
        const hours = now.getHours().toString().padStart(2, '0');
        const minutes = now.getMinutes().toString().padStart(2, '0');
        const timeString = `${hours}:${minutes}`;
        setFormData(prev => ({ ...prev, timeToAdminister: timeString }));
      }
    }
  }, [formData.dose]);

  useEffect(() => {
    axios.get(`${API_BASE_URL}/get_preop_order_next_ip`)
      .then(response => {
        setData(response.data);
        setFormData(prev => ({
          ...prev,
          ipNumber: response.data.next_ip_no
        }));

      })
      .catch(error => {
        console.error('Error fetching ip_no:', error);
        setError('Failed to fetch OT bookings');
      });
  }, []);
  // Fetch all patients
  useEffect(() => {
    setLoading(true);
    axios.get(`${API_BASE_URL}/get_all_patients`, {
      withCredentials: true, // sends cookies
    })
      .then(response => {
        setPatients(response.data);
        setLoading(false);
      })
      .catch(error => {
        console.error('Error fetching patients:', error);
        setError('Failed to fetch patients');
        setLoading(false);
      });
  }, []);

  // Filter patients based on OT bookings
  useEffect(() => {
    if (patients.length > 0 && data.length > 0) {
      const bookedUHIDs = new Set(data.map(b => b.uhid_no));
      const availablePatients = patients.filter(p => !bookedUHIDs.has(p.uhid_no));
      setFilteredPatients(availablePatients);
    } else {
      setFilteredPatients(patients);
    }
  }, [patients, data]);

  const loadExistingData = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/get_preop_order/${uhid_no}`);
      if (response.data) {
        setSuccess('success');
        setFormData(prev => ({
          ...prev,
          ...response.data,
          // Ensure arrays and objects are properly handled
          bloodOrders: typeof response.data.bloodOrders === 'string'
            ? JSON.parse(response.data.bloodOrders)
            : response.data.bloodOrders || {},
          labsToDo: typeof response.data.labsToDo === 'string'
            ? JSON.parse(response.data.labsToDo)
            : response.data.labsToDo || [],
          imagingToBeAvailable: typeof response.data.imagingToBeAvailable === 'string'
            ? JSON.parse(response.data.imagingToBeAvailable)
            : response.data.imagingToBeAvailable || []
        }));
        return true;
      }
    } catch (error) {
      // If no existing data found, that's okay - we'll create new
      console.log('No existing preop order found, creating new one');
    }
    return false;
  };

  const fetchPatientData = async (uhidNo) => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_BASE_URL}/get_patient/${uhid_no}`, {
        withCredentials: true, // sends cookies
      });

      console.log(response.data);
      setFormData(prev => ({
        ...prev,
        name: response.data.name,
        age: response.data.age || "",
        sex: response.data.sex || "",
      }));
    } catch (error) {
      console.error('Error fetching patient data:', error);
      // setError('Failed to fetch patient data. Please try again.');
    } finally {
      setLoading(false);
    }
  };


  const handleChange = (event) => {
    const { name, type, checked, value } = event.target;

    // Validate field on change
    if (validationErrors[name]) {
      setValidationErrors(prev => ({ ...prev, [name]: '' }));
    }

    const autoFillMap = {
      patient_name: {
        list: patients,
        matchField: "Name",
        valueField: "uhid_no",
        idField: "uhid_no"
      },
      primary_surgeon_name: {
        list: surgeonList,
        matchField: "name",
        valueField: "id",
        idField: "primary_surgeon_id"
      }
    };

    // Handle surgery selection with auto-fill
    if (name === 'type_of_surgery' || name === 'diagnosis') {
      const selectedSurgery = surgeryData.find(
        surgery => surgery.diagnosisDescription === value
      );

      if (selectedSurgery) {
        setFormData(prev => ({
          ...prev,
          diagnosis: value,
          surgical_code: selectedSurgery.icd10Code,
          procedure_code: selectedSurgery.procedureCode,
          procedureDescription: selectedSurgery.procedureDescription,
          proposedSurgery: selectedSurgery.procedureDescription
        }));
      } else {
        setFormData(prev => ({
          ...prev,
          [name]: value
        }));
      }
    } else if (autoFillMap[name]) {
      const config = autoFillMap[name];
      const selected = config.list.find(
        item => item[config.matchField] === value
      );

      setFormData(prev => ({
        ...prev,
        [name]: value,
        [config.idField]: selected ? selected[config.valueField] : ""
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : value
      }));
    }
  };

  const navigate = useNavigate();

  const handleBloodOrderChange = (component, value) => {
    const numValue = parseInt(value) || 0;
    if (numValue < 0 || numValue > 20) {
      setValidationErrors(prev => ({
        ...prev,
        [`bloodOrder_${component}`]: 'Units should be between 0-20'
      }));
      return;
    }

    setValidationErrors(prev => ({
      ...prev,
      [`bloodOrder_${component}`]: ''
    }));

    setFormData(prev => ({
      ...prev,
      bloodOrders: {
        ...prev.bloodOrders,
        [component]: numValue
      }
    }));
  };

  const surgeonList = [
    { id: 101, name: "Dr. Alice Smith" },
    { id: 102, name: "Dr. John Doe" },
    { id: 103, name: "Dr. Rachel Green" },
    { id: 104, name: "Dr. Ross Geller" },
    { id: 105, name: "Dr. Monica Geller" },
    { id: 106, name: "Dr. Chandler Bing" },
    { id: 107, name: "Dr. Joey Tribbiani" },
    { id: 108, name: "Dr. Sheldon Cooper" },
    { id: 109, name: "Dr. Leonard Hofstadter" },
    { id: 110, name: "Dr. Amy Farrah Fowler" },
  ];

  const sendEmailNotification = async (isUpdate = false) => {
    setEmailLoading(true);
    try {
      const emailData = {
        uhid: formData.uhid,
        patientName: formData.name,
        surgeonName: formData.surgeonName,
        diagnosis: formData.diagnosis,
        proposedSurgery: formData.proposedSurgery,
        scheduledDatetime: formData.scheduledDatetime,
        isUpdate: isUpdate
      };

      const response = await axios.post(`${API_BASE_URL}/send_preop_notification`, emailData);
      setEmailSuccess(response.data.message);

      // Clear success message after 5 seconds
      setTimeout(() => {
        setEmailSuccess("");
      }, 5000);

    } catch (error) {
      console.error('Email sending failed:', error);
      setError('Failed to send email notification to nurses');
    } finally {
      setEmailLoading(false);
    }
  };

  const validateForm = () => {
    const errors = {};
    const requiredFields = [
      'uhid', 'ipNumber', 'name', 'age', 'sex', 'room',
      'diagnosis', 'proposedSurgery', 'surgeonName'
    ];

    requiredFields.forEach(field => {
      if (!formData[field] || formData[field] === '') {
        errors[field] = 'This field is required';
      }
    });

    // Validate scheduled datetime
    if (formData.scheduledDatetime && formData.scheduledDatetime < getCurrentDateTime()) {
      errors.scheduledDatetime = 'Surgery cannot be scheduled in the past';
    }

    // Validate age
    const age = parseInt(formData.age);
    if (formData.age && (age < 0 || age > 150)) {
      errors.age = 'Please enter a valid age between 0-150';
    }

    // Validate if minor needs guardian info
    if (age < 18 && (!formData.guardianName || !formData.guardianContact)) {
      if (!formData.guardianName) errors.guardianName = 'Guardian name required for minors';
      if (!formData.guardianContact) errors.guardianContact = 'Guardian contact required for minors';
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      setError('Please fix all validation errors before submitting. Check the highlighted fields above.');
      // Scroll to top to show error alert
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    if (!formData.finalized) {
      setError('Please confirm orders as finalized by checking the checkbox before submitting.');
      // Scroll to finalized checkbox
      const finalizedElement = document.querySelector('[name="finalized"]');
      if (finalizedElement) {
        finalizedElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      return;
    }

    setLoading(true);
    setError(''); // Clear any previous errors

    // Prepare data for submission
    const submitData = {
      ...formData,
      bloodOrders: JSON.stringify(formData.bloodOrders),
      labsToDo: JSON.stringify(formData.labsToDo),
      imagingToBeAvailable: JSON.stringify(formData.imagingToBeAvailable),
      uhid: parseInt(formData.uhid) || 0,
      ipNumber: parseInt(formData.ipNumber) || 0,
      estimatedBloodLoss: parseInt(formData.estimatedBloodLoss) || 0,
      status: 'pending'
    };

    try {
      let response;
      let isUpdate = false;

      // Check if we are updating or creating a new record
      if (success === "success") {
        // Update existing record
        response = await axios.put(`${API_BASE_URL}/update_preop_order/${uhid_no}`, submitData, { withCredentials: true });
        showNotification('success', 'Pre-operative orders updated successfully!');
        isUpdate = true;
      } else {
        // Create new record
        response = await axios.post(`${API_BASE_URL}/preop_form_submit`, submitData, { withCredentials: true });
        showNotification('success', 'Pre-operative orders submitted successfully!');
      }

      // Clear draft after successful submission
      clearDraft(DRAFT_KEYS.PREOPERATIVE_ORDERS, uhid_no);

      // Send email notification
      await sendEmailNotification(isUpdate);

      // Show success message briefly before navigating
      setTimeout(() => {
        navigate(`/mainPage/${uhid_no}`, {
          state: { from: 'PreOperativePhase', status: 'success', step: 1 },
        });
      }, 2000);

    } catch (error) {
      console.error('Submission error:', error);
      const errorMessage = error.response?.data?.error || error.message || 'Unknown error occurred';
      showNotification('error', `Failed to submit pre-operative orders: ${errorMessage}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <ThemeProvider theme={theme}>
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Paper sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Button
              startIcon={<ArrowBackIcon />}
              component={Link}
              to={`/mainPage/${uhid_no}`}
              state={{ from: 'PreOperativePhase', status: 'success', step: 1 }}
              sx={{ color: '#00695c', textTransform: 'none', fontSize: '1rem' }}
            >
              Back
            </Button>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <img src="/logo.png" alt="Logo" className="w-8 h-8" />
              <Typography variant="h6" sx={{ color: '#00695c' }}>OTMS</Typography>
            </Box>
          </Box>

          <Typography variant="h4" sx={{ color: '#00695c', mb: 2, fontWeight: 'bold' }}>
            PRE OPERATIVE ORDERS
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              <Typography variant="body1" fontWeight="bold">Form Error:</Typography>
              {error}
            </Alert>
          )}



          {emailSuccess && (
            <Alert severity="success" sx={{ mb: 2 }}>
              <Typography variant="body1" fontWeight="bold">Email Notification:</Typography>
              {emailSuccess}
            </Alert>
          )}

          {success === "success" && (
            <Alert severity="info" sx={{ mb: 2 }}>
              <Typography variant="body1" fontWeight="bold">Existing Record:</Typography>
              You are updating an existing preoperative order. Any changes will overwrite the previous version.
            </Alert>
          )}

          <form onSubmit={handleSubmit}>
            <Accordion defaultExpanded>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography>Patient & Surgery Details</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="UHID No"
                      value={formData.uhid}
                      fullWidth
                      InputProps={{ readOnly: true }}
                      sx={{ backgroundColor: '#f5f5f5' }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      name="ipNumber"
                      label="IP No"
                      value={formData.ipNumber}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      fullWidth
                      type="number"
                      required
                      error={!!validationErrors.ipNumber}
                      helperText={validationErrors.ipNumber}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Autocomplete
                      options={commonNames}
                      value={formData.name}
                      onChange={(e, newValue) => setFormData(prev => ({ ...prev, name: newValue || '' }))}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          name="name"
                          label="Patient Name"
                          required
                          error={!!validationErrors.name}
                          helperText={validationErrors.name}
                          onBlur={handleBlur}
                        />
                      )}
                      freeSolo
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      name="age"
                      label="Age"
                      value={formData.age}
                      onChange={handleChange}
                      fullWidth
                      type="number"
                      required
                      error={!!validationErrors.age}
                      helperText={validationErrors.age}
                      inputProps={{ min: 0, max: 150 }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      select
                      name="sex"
                      label="Sex"
                      value={formData.sex}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      fullWidth
                      required
                      error={!!validationErrors.sex}
                      helperText={validationErrors.sex}
                    >
                      <MenuItem value="Male">Male</MenuItem>
                      <MenuItem value="Female">Female</MenuItem>
                      <MenuItem value="Other">Other</MenuItem>
                    </TextField>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      select
                      label="Room No"
                      name="room"
                      value={formData.room}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      fullWidth
                      required
                      error={!!validationErrors.room}
                      helperText={validationErrors.room}
                    >
                      {roomNumbers.map((room) => (
                        <MenuItem key={room} value={room}>
                          {room}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                  <Grid item xs={12}>
                    <FormControl fullWidth required error={!!validationErrors.diagnosis}>
                      <InputLabel id="type-of-surgery-label">Type of Surgery/Procedure</InputLabel>
                      <Select
                        labelId="type-of-surgery-label"
                        id="type-of-surgery"
                        value={formData.diagnosis || ''}
                        label="Type of Surgery/Procedure"
                        onChange={(event) =>
                          handleChange({
                            target: { name: 'diagnosis', value: event.target.value },
                          })
                        }
                      >
                        {surgeryData.map((surgery, index) => (
                          <MenuItem key={index} value={surgery.diagnosisDescription}>
                            {surgery.diagnosisDescription}
                          </MenuItem>
                        ))}
                      </Select>
                      {validationErrors.diagnosis && (
                        <FormHelperText>{validationErrors.diagnosis}</FormHelperText>
                      )}
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Surgical Code (ICD-10)"
                      name="surgical_code"
                      value={formData.surgical_code}
                      onChange={handleChange}
                      required
                      fullWidth
                      InputProps={{ readOnly: true }}
                      helperText="Auto-filled when surgery is selected"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Procedure Code (CPT)"
                      name="procedure_code"
                      value={formData.procedure_code}
                      onChange={handleChange}
                      required
                      fullWidth
                      InputProps={{ readOnly: true }}
                      helperText="Auto-filled when surgery is selected"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      label="Procedure Description"
                      name="proposedSurgery"
                      value={formData.proposedSurgery}
                      onChange={handleChange}
                      required
                      fullWidth
                      InputProps={{ readOnly: true }}
                      helperText="Auto-filled when surgery is selected"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      select
                      label="Primary Surgeon Name"
                      name="surgeonName"
                      value={formData.surgeonName}
                      onChange={handleChange}
                      required
                      fullWidth
                      error={!!validationErrors.surgeonName}
                      helperText={validationErrors.surgeonName}
                    >
                      {surgeonList.map((s) => (
                        <MenuItem key={s.id} value={s.name}>{s.name}</MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      name="scheduledDatetime"
                      type="datetime-local"
                      label="Scheduled Date/Time"
                      value={formData.scheduledDatetime}
                      onChange={handleChange}
                      fullWidth
                      inputProps={{
                        min: getCurrentDateTime(),
                      }}
                      InputLabelProps={{ shrink: true }}
                      error={!!validationErrors.scheduledDatetime}
                      helperText={validationErrors.scheduledDatetime || "Surgery must be scheduled for future date/time"}
                    />
                  </Grid>
                </Grid>
              </AccordionDetails>
            </Accordion>

            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography>Consent Orders</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <FormControlLabel
                      control={<Checkbox name="surgicalConsent" checked={formData.surgicalConsent} onChange={handleChange} />}
                      label="Surgical Consent"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormControlLabel
                      control={<Checkbox name="highRiskConsent" checked={formData.highRiskConsent} onChange={handleChange} />}
                      label="High Risk Consent"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormControlLabel
                      control={<Checkbox name="bloodTransfusionConsent" checked={formData.bloodTransfusionConsent} onChange={handleChange} />}
                      label="Blood Transfusion Consent"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormControlLabel
                      control={<Checkbox name="anesthesiaConsent" checked={formData.anesthesiaConsent} onChange={handleChange} />}
                      label="Anesthesia Consent"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      name="guardianName"
                      label={parseInt(formData.age) < 18 ? "Legal Guardian Name *" : "Legal Guardian Name"}
                      value={formData.guardianName}
                      onChange={handleChange}
                      fullWidth
                      required={parseInt(formData.age) < 18}
                      error={!!validationErrors.guardianName}
                      helperText={validationErrors.guardianName || (parseInt(formData.age) < 18 ? "Required for patients under 18" : "")}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <PhoneInput
                      country={'in'}
                      value={formData.guardianContact}
                      onChange={(value) =>
                        setFormData((prev) => ({ ...prev, guardianContact: value }))
                      }
                      enableSearch
                      inputProps={{
                        name: 'guardianContact',
                        required: parseInt(formData.age) < 18,
                        autoFocus: false,
                      }}
                      containerStyle={{ width: '100%' }}
                      inputStyle={{
                        width: '100%',
                        height: '56px',
                        borderColor: validationErrors.guardianContact ? '#f44336' : undefined
                      }}
                      buttonStyle={{ height: '56px' }}
                    />
                    {validationErrors.guardianContact && (
                      <FormHelperText error>{validationErrors.guardianContact}</FormHelperText>
                    )}
                    {parseInt(formData.age) < 18 && (
                      <FormHelperText>Required for patients under 18</FormHelperText>
                    )}
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      name="consentComments"
                      label="Consent Comments"
                      value={formData.consentComments}
                      onChange={handleChange}
                      fullWidth
                      multiline
                      rows={2}
                    />
                  </Grid>
                </Grid>
              </AccordionDetails>
            </Accordion>

            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography>Blood & Product Orders</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Grid container spacing={2}>
                  {bloodComponents.map((comp) => (
                    <Grid item xs={12} sm={6} key={comp}>
                      <TextField
                        type="number"
                        label={`${comp} Units`}
                        value={formData.bloodOrders[comp] || ''}
                        onChange={(e) => handleBloodOrderChange(comp, e.target.value)}
                        fullWidth
                        inputProps={{ min: 0, max: 20 }}
                        error={!!validationErrors[`bloodOrder_${comp}`]}
                        helperText={validationErrors[`bloodOrder_${comp}`] || "Max 20 units"}
                      />
                    </Grid>
                  ))}
                  <Grid item xs={12} sm={6}>
                    <FormControlLabel
                      control={<Checkbox name="sopNeeded" checked={formData.sopNeeded} onChange={handleChange} />}
                      label="SOP Needed"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Autocomplete
                      options={bloodGroups}
                      value={formData.bloodGroup}
                      onChange={(e, newValue) => setFormData(prev => ({ ...prev, bloodGroup: newValue || '' }))}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          name="bloodGroup"
                          label="Blood Group"
                          helperText="Select patient's blood group"
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <FormControlLabel
                      control={<Checkbox name="prdcAttached" checked={formData.prdcAttached} onChange={handleChange} />}
                      label="PRDC Attached"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      name="transfusionNotes"
                      label="Transfusion Notes"
                      value={formData.transfusionNotes}
                      onChange={handleChange}
                      fullWidth
                      multiline
                      rows={2}
                    />
                  </Grid>
                </Grid>
              </AccordionDetails>
            </Accordion>

            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography>Antibiotic & Medications</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Autocomplete
                      options={antibiotics}
                      value={formData.antibiotic || null}
                      onChange={(e, newValue) => setFormData(prev => ({ ...prev, antibiotic: newValue || '' }))}
                      renderInput={(params) => <TextField {...params} label="Antibiotic Chosen" />}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      name="dose"
                      type='number'
                      label="Dose (minutes before surgery)"
                      value={formData.dose}
                      onChange={handleChange}
                      fullWidth
                      inputProps={{ min: 15, max: 120 }}
                      helperText="Time in minutes (15-120 min recommended)"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      name="timeToAdminister"
                      label="Time to Administer"
                      type="time"
                      value={formData.timeToAdminister}
                      onChange={handleChange}
                      InputLabelProps={{ shrink: true }}
                      fullWidth
                      helperText="Auto-calculated based on dose timing"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      name="additionalAntibiotics"
                      label="Additional Antibiotics"
                      value={formData.additionalAntibiotics}
                      onChange={handleChange}
                      fullWidth
                      multiline
                      rows={2}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Autocomplete
                      options={antiseptics}
                      value={formData.skinPrep || null}
                      onChange={(e, newValue) => setFormData(prev => ({ ...prev, skinPrep: newValue || '' }))}
                      renderInput={(params) => <TextField {...params} label="Skin Prep Type" />}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      name="preAnestheticMeds"
                      label="Pre-Anesthetic Medications"
                      value={formData.preAnestheticMeds}
                      onChange={handleChange}
                      fullWidth
                      multiline
                      rows={2}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <FormControlLabel
                      control={<Checkbox name="preOpSedation" checked={formData.preOpSedation} onChange={handleChange} />}
                      label="Pre-op Sedation Required"
                    />
                  </Grid>
                </Grid>
              </AccordionDetails>
            </Accordion>

            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography>Anticoagulation</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <FormControlLabel
                      control={<Checkbox name="onAnticoagulants" checked={formData.onAnticoagulants} onChange={handleChange} />}
                      label="On Anticoagulants"
                    />
                  </Grid>
                  {formData.onAnticoagulants && (
                    <>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          name="lastDose"
                          type="datetime-local"
                          label="Last Dose Date/Time"
                          value={formData.lastDose}
                          inputProps={{
                            max: getCurrentDateTime(),
                          }}
                          onChange={handleChange}
                          fullWidth
                          InputLabelProps={{ shrink: true }}
                          error={!!validationErrors.lastDose}
                          helperText={validationErrors.lastDose || "Cannot be in the future"}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Autocomplete
                          options={bridgingPlans}
                          value={formData.bridgingPlan || null}
                          onChange={(e, newValue) => setFormData(prev => ({ ...prev, bridgingPlan: newValue || '' }))}
                          renderInput={(params) => <TextField {...params} label="Bridging Plan" />}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          name="stopDate"
                          label="Stop Date"
                          type="date"
                          value={formData.stopDate}
                          inputProps={{
                            min: getTodayDate(),
                            max: formData.scheduledDatetime ? formData.scheduledDatetime.slice(0, 10) : undefined
                          }}
                          onChange={handleChange}
                          fullWidth
                          InputLabelProps={{ shrink: true }}
                          error={!!validationErrors.stopDate}
                          helperText={validationErrors.stopDate || "Auto-suggested based on surgery date"}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <FormControlLabel
                          control={<Checkbox name="postOpThromboprophylaxis" checked={formData.postOpThromboprophylaxis} onChange={handleChange} />}
                          label="Thromboprophylaxis Needed Post-Op"
                        />
                      </Grid>
                    </>
                  )}
                  <Grid item xs={12}>
                    <TextField
                      name="allergyReview"
                      label="Allergy Review"
                      value={formData.allergyReview}
                      onChange={handleChange}
                      fullWidth
                      multiline
                      rows={2}
                      placeholder="Document any known allergies or adverse reactions"
                    />
                  </Grid>
                </Grid>
              </AccordionDetails>
            </Accordion>

            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography>Investigations</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Autocomplete
                      multiple
                      options={labs}
                      value={formData.labsToDo}
                      onChange={(e, newVal) => setFormData(prev => ({ ...prev, labsToDo: newVal }))}
                      renderInput={(params) => <TextField {...params} label="Labs to be Done" />}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Autocomplete
                      multiple
                      options={imaging}
                      value={formData.imagingToBeAvailable}
                      onChange={(e, newVal) => setFormData(prev => ({ ...prev, imagingToBeAvailable: newVal }))}
                      renderInput={(params) => <TextField {...params} label="Imaging to be Available in OT" />}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <FormControlLabel
                      control={<Checkbox name="cardiologyClearance" checked={formData.cardiologyClearance} onChange={handleChange} />}
                      label="Cardiology Clearance Required"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <FormControlLabel
                      control={<Checkbox name="anesthClearance" checked={formData.anesthClearance} onChange={handleChange} />}
                      label="Anesthesiology Clearance Required"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      name="otherSpecialtyClearance"
                      label="Other Specialty Clearance"
                      value={formData.otherSpecialtyClearance}
                      onChange={handleChange}
                      fullWidth
                      multiline
                      rows={2}
                    />
                  </Grid>
                </Grid>
              </AccordionDetails>
            </Accordion>

            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography>Surgeon Instructions</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <TextField
                      name="additionalEquipments"
                      label="Additional Equipment Needed"
                      value={formData.additionalEquipments}
                      onChange={handleChange}
                      fullWidth
                      multiline
                      rows={2}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      name="criticalNotes"
                      label="Critical Considerations"
                      value={formData.criticalNotes}
                      onChange={handleChange}
                      fullWidth
                      multiline
                      rows={2}
                      placeholder="Important notes for the surgical team"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      name="estimatedBloodLoss"
                      type="number"
                      label="Estimated Blood Loss (ml)"
                      value={formData.estimatedBloodLoss}
                      onChange={handleChange}
                      fullWidth
                      inputProps={{ min: 0, max: 10000 }}
                      error={!!validationErrors.estimatedBloodLoss}
                      helperText={validationErrors.estimatedBloodLoss || "Enter estimated blood loss in milliliters"}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <FormControlLabel
                      control={<Checkbox name="siteMarking" checked={formData.siteMarking} onChange={handleChange} />}
                      label="Surgical Site Marking Required"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Autocomplete
                      options={positioningOptions}
                      value={formData.positioning || null}
                      onChange={(e, newValue) => setFormData(prev => ({ ...prev, positioning: newValue || '' }))}
                      renderInput={(params) => <TextField {...params} label="Positioning Requirement" />}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      name="surgeonNotes"
                      label="Other Instructions"
                      value={formData.surgeonNotes}
                      onChange={handleChange}
                      fullWidth
                      multiline
                      rows={2}
                    />
                  </Grid>
                </Grid>
              </AccordionDetails>
            </Accordion>

            <Box sx={{ mt: 3, mb: 2 }}>
              <FormControlLabel
                control={<Checkbox name="finalized" checked={formData.finalized} onChange={handleChange} />}
                label="Confirm Orders as Finalized"
              />
              <FormHelperText>
                Please review all details carefully before finalizing the orders
              </FormHelperText>

              {!formData.finalized && (
                <Alert severity="warning" sx={{ mt: 2 }}>
                  <Typography variant="body2">
                    <strong>Orders Not Finalized:</strong> You must confirm the orders as finalized before submission.
                  </Typography>
                </Alert>
              )}

              {formData.finalized && Object.keys(validationErrors).some(key => validationErrors[key]) && (
                <Alert severity="error" sx={{ mt: 2 }}>
                  <Typography variant="body2">
                    <strong>Cannot Submit:</strong> Please fix all validation errors before finalizing submission.
                  </Typography>
                </Alert>
              )}

              {formData.finalized && !Object.keys(validationErrors).some(key => validationErrors[key]) && (
                <Alert severity="success" sx={{ mt: 2 }}>
                  <Typography variant="body2">
                    <strong>Ready to Submit:</strong> All validations passed. Orders are ready for submission.
                  </Typography>
                </Alert>
              )}
            </Box>

            <Button
              type="submit"
              variant="contained"
              color="primary"
              sx={{ marginTop: 2 }}
              fullWidth
              disabled={loading || emailLoading || !formData.finalized}
              startIcon={loading || emailLoading ? <CircularProgress size={20} /> : null}
            >
              {loading
                ? 'Processing...'
                : success === "success"
                  ? 'Update Orders & Notify Nurses'
                  : 'Submit Orders & Notify Nurses'
              }
            </Button>

            {emailLoading && (
              <Alert severity="info" sx={{ mt: 2, display: 'flex', alignItems: 'center' }}>
                <CircularProgress size={20} sx={{ mr: 2 }} />
                <Box>
                  <Typography variant="body1" fontWeight="bold">Processing Email Notification</Typography>
                  <Typography variant="body2">
                    Sending notification to nursing staff and relevant departments...
                  </Typography>
                </Box>
              </Alert>
            )}

            {loading && (
              <Alert severity="info" sx={{ mt: 2, display: 'flex', alignItems: 'center' }}>
                <CircularProgress size={20} sx={{ mr: 2 }} />
                <Box>
                  <Typography variant="body1" fontWeight="bold">Saving Orders</Typography>
                  <Typography variant="body2">
                    {success === "success"
                      ? "Updating preoperative orders in the system..."
                      : "Creating new preoperative orders in the system..."
                    }
                  </Typography>
                </Box>
              </Alert>
            )}

            <Divider sx={{ my: 2 }} />

            <Box sx={{ mb: 2 }}>
              <Alert severity="info" variant="outlined">
                <Typography variant="body2">
                  <strong>Note:</strong> All form data is automatically saved as draft every 2 seconds.
                  You can safely close and return to continue editing.
                </Typography>
              </Alert>
            </Box>

            <DraftSaveButton
              formKey={DRAFT_KEYS.PREOPERATIVE_ORDERS}
              uhidNo={uhid_no}
              formData={formData}
              onDraftLoaded={(draftData) => {
                setFormData(prev => ({ ...prev, ...draftData }));
                setEmailSuccess('Draft loaded successfully! You can continue editing from where you left off.');
              }}
              variant="outlined"
              size="large"
              isUpdate={success === "success"}
            />
          </form>
        </Paper>
      </Container>
    </ThemeProvider>
  );
};

export default PreoperativeOrders;